import Axios, { AxiosRequestConfig, AxiosResponse, Canceler } from 'axios';
import { getErrorMsg } from './i18n';
import { Alert, AlertProps, Message } from '@fx-ui/fine-design';
import Intl from '@fx-ui/intl';
import { getQueryRouter } from './site';
import { SITE_ROUTER, URL_PARAMETER } from '~/constants';
import { assign, ECode, get, getUUID, indexOf, redirect } from '@libs/shared';

/**
 * ajax请求
 *
 * @param url 请求地址
 * @param data 请求数据
 * @param config
 * @param config.options 请求配置
 * @param config.customHandleErrorCodes 走自定义错误处理方式的errorCode列表
 * @param config.customHandleHttpCodes 走自定义错误处理方式的httpCode列表
 * @param config.customHandleErrorFn 走自定义错误处理方式的函数
 * @param config.cancelExecutor
 * @param config.cancelReject
 */
export const post = <T = any>(
    url: string,
    data?: object,
    config: {
        options?: AxiosRequestConfig<object>;
        cancelExecutor?: (c: Canceler) => void;
        cancelReject?: boolean;
        customHandleErrorCodes?: (number | string)[];
        customHandleHttpCodes?: number[];
        customHandleErrorFn?: (errCode: number | string) => boolean; // 返回true示需要自定义处理
    } = {}
) => {
    const options: Record<string, any> = {
        headers: {
            'X-CSRF-Token': window.jdy_csrf_token,
            'X-JDY-VER': window.jdy_config?.version,
            'X-Request-ID': getUUID() // 请求唯一ID
        }
    };
    const o = assign(options, config.options);
    const postData = data || {};
    if (config.cancelExecutor) {
        assign(o, {
            cancelToken: new Axios.CancelToken(config.cancelExecutor)
        });
    }
    return new Promise<AxiosResponse<T>>((resolve, reject) => {
        Axios.post(url, postData, o)
            .then((response) => {
                resolve(response);
            })
            .catch((error) => {
                if (Axios.isCancel(error)) {
                    if (config.cancelReject) {
                        return reject(error);
                    }
                    return;
                }
                const httpCode = get(error, 'response.status');
                if (indexOf(config.customHandleHttpCodes, httpCode) > -1) {
                    return reject(error);
                }
                const resData = get(error, 'response.data');
                const errorCode = get(resData, 'code');
                switch (httpCode) {
                    case 400:
                        if (
                            indexOf(config.customHandleErrorCodes, errorCode) > -1 ||
                            (config.customHandleErrorFn && config.customHandleErrorFn(errorCode))
                        ) {
                            return reject(error);
                        }
                        if (errorCode) {
                            Message.add({
                                messageType: 'toast',
                                type: 'warning',
                                message: getErrorMsg(resData)
                            });
                        } else {
                            // 错误的请求
                            Message.add({
                                messageType: 'toast',
                                type: 'warning',
                                message: Intl.get('error.status.400')
                            });
                        }
                        break;
                    case 401:
                        // 未登录
                        Message.add({
                            messageType: 'toast',
                            type: 'warning',
                            message: Intl.get('error.status.401')
                        });
                        break;
                    case 402:
                        // 会话过期
                        Message.add({
                            messageType: 'toast',
                            type: 'warning',
                            message: Intl.get('error.status.402')
                        });
                        break;
                    case 403:
                        // 没有数据请求权限
                        Message.add({
                            messageType: 'toast',
                            type: 'warning',
                            message: Intl.get('error.status.403')
                        });
                        break;
                    case 404:
                        // 找不到数据资源
                        Message.add({
                            messageType: 'toast',
                            type: 'warning',
                            message: Intl.get('error.status.404')
                        });
                        break;
                    case 409: {
                        const redirectToSignIn = () => {
                            redirect(
                                getQueryRouter(
                                    [
                                        {
                                            key: URL_PARAMETER.REDIRECT_URI,
                                            value: encodeURIComponent(window.location.href)
                                        }
                                    ],
                                    SITE_ROUTER.AUTHENTICATION.SIGNOUT
                                )
                            );
                        };
                        const showErrorAlert = (config: Partial<AlertProps>) => {
                            Alert.openAlert({
                                type: 'error',
                                text4Cancel: null,
                                title: getErrorMsg(resData),
                                onOk: redirectToSignIn,
                                ...config
                            });
                        };

                        // 兼容 fx-corp /portal 的 ECode 和 fx-code /open 的 bizCode。
                        switch (errorCode) {
                            case ECode.USER_ALREADY_LOGGED_IN_ELSEWHERE:
                            case 41025: // 账号在其他地方登录
                            case ECode.USER_SINGLE_DEVICE_LOGIN_COOKIE_EXPIRED:
                            case 41050: // 「禁止同时登录」Cookie 过期
                                showErrorAlert({
                                    message: Intl.get('login.single.device.desc')
                                });
                                break;
                            case ECode.MEMBER_IS_FROZEN:
                            case 60006:
                                // 当前成员被禁用
                                showErrorAlert({
                                    message: Intl.get('error.tip.frozen')
                                });
                                break;
                            default:
                                // 不在当前企业
                                showErrorAlert({
                                    title: Intl.get('error.status.corpSwitch'),
                                    message: Intl.get('error.status.corpSwitch.tip'),
                                    text4Ok: Intl.get('iSee'),
                                    onOk: undefined
                                });
                        }
                        break;
                    }
                    case 413:
                        // 请求实体过大
                        Message.add({
                            messageType: 'toast',
                            type: 'warning',
                            message: Intl.get('error.status.413')
                        });
                        break;
                    case 429:
                        // 访问人数过多
                        Message.add({
                            messageType: 'toast',
                            type: 'warning',
                            message: Intl.get('error.status.429')
                        });
                        break;
                    case 0:
                        // 请求中断
                        break;
                    default:
                        Message.add({
                            messageType: 'toast',
                            type: 'warning',
                            message: Intl.get('error.status.fail')
                        });
                        break;
                }
                reject(error);
            });
    });
};
